import { AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios'
import { unauthorizeLogout } from '../context/UserAuthentication/actions';
import { getRequestId, getUser } from '../helpers/helperMethods';
import store from '../redux/store/store';
import { bugSnagNotifyAPIError } from '../helpers/bugSnagHelper';
import { isTokenExpired, refreshToken } from '../helpers/authentication';

const { dispatch } = store;
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;
//Queue to manage request promises, created while the token is being fetched 
let requestPromiseQueue: ((token: any) => void)[] = [];
//Function that will resolve all the promises queued, after new token being fetched
function onRefreshed(token: any) {
  requestPromiseQueue.forEach((callback) => callback(token));
  requestPromiseQueue = [];
}
//Function to Queue request Promises which will require new refresed tokens
function queueRefreshToken(requestPromise: (token: any) => void) {
  requestPromiseQueue.push(requestPromise);
}

export const requestHandler: any = async (request: AxiosRequestConfig) => {
  let authInfo = getUser();
  const { shouldRefetchToken: shouldRefresh, shouldLogout } = await isTokenExpired(authInfo);
  let requestId = getRequestId();

  // If both tokens (refreshed + auth) are expired
  if (shouldLogout) {
    unauthorizeLogout(dispatch);
    // Returning the original request prevents cancelToken-related errors.
    // If not returned, the base method may access an undefined `this.source.token`,
    return Promise.reject(new Error("Session expired"));
  }
  //code block to fetch new token and queue rest requests
  if (shouldRefresh) {
    if (!isRefreshing && !refreshPromise) {
      isRefreshing = true;
      const currentAuthUser = getUser();
      refreshPromise = refreshToken(dispatch, currentAuthUser)
        .then((newToken) => {
          onRefreshed(newToken);
          return newToken;
        })
        .catch((error) => {
          throw error;
        })
        .finally(() => {
          isRefreshing = false; 
          refreshPromise = null;
        });
        ;
    }
    // [IMPORTANT] - This code block will create new promise against each async request and push them in queue, 
    // they will be resolved once they get new refreshed tokens.
    const newUserTokens = await new Promise<any>((resolve) => {
      queueRefreshToken(resolve);
    });
    if (newUserTokens?.token) {
      request.headers['Authorization'] = `Bearer ${newUserTokens.token}`
      request.headers['token'] = newUserTokens.token;
      request.headers['Refresh-token'] = newUserTokens.refresh_token;
    }
  }
  else {
    request.headers['Authorization'] = request.headers['Authorization'] || 'Bearer ' + authInfo.token;
    request.headers['token'] = request.headers['token'] || authInfo.authentication_token;
    request.headers['Refresh-token'] = request.headers['Refresh-token'] || "";
  }
  request.headers['request-id'] = request.headers['request-id'] || requestId;
  return request;
};

export const successResponseHandler = (response: AxiosResponse) => {
  return response;
};

export const errorResponseHandler = (error: AxiosError) => {
  if (error?.response?.status === 401) {
    unauthorizeLogout(dispatch);
    bugSnagNotifyAPIError("Authentication Error", {}, `Request failed with status code 401`);
  }
  // Handles Server Errors for Bugsnag reporting
  if (error?.response?.status >= 500 && error?.response?.status < 600) {
    bugSnagNotifyAPIError("Server Error occurred", error?.response, `Server Error with status code ${error?.response?.status}`);
  }
  
  // Handles Network Errors for Bugsnag reporting
  if (!error?.response) {
    bugSnagNotifyAPIError("Network error occurred", error, error?.message);
  }
  return Promise.reject(error);
};